import { createContext, useCallback, useContext, useEffect, useMemo, useState, type PropsWithChildren } from "react";

export type Locale = "zh-CN" | "en-US";
type MessageValues = Record<string, string | number>;
type I18nContextValue = { locale: Locale; setLocale: (locale: Locale) => void; t: (message: string, values?: MessageValues) => string };

const STORAGE_KEY = "yunliu-locale";

const englishMessages: Record<string, string> = {
  '会员': 'Membership',
  '我的': 'My account',
  '会员分类': 'Membership categories',
  '选择适合你的会员权益': 'Choose the membership that fits you',
  '会员充值与高速流量包分开选择，价格和有效期清晰展示。': 'Choose membership or extra data separately, with clear pricing and expiry.',
  '选择会员': 'Choose a membership',
  '选择流量包': 'Choose a data package',
  '每30天高速流量': 'High-speed data per 30 days',
  '下次高速流量发放': 'Next high-speed data allowance',
  '会员有效期以所选套餐为准，包含高速流量与多设备加速。': 'Membership lasts for the selected plan period and includes high-speed data for multiple devices.',
  '加购流量随当前会员周期到期，不会延长会员时长。': 'Extra data expires with your current membership and does not extend it.',
  '价格实时同步': 'Prices synced live',
  '价格以会员中心为准': 'Final prices are shown in the account center',
  '{days} 天有效': '{days} days',
  '已选择': 'Selected',
  '前往会员中心开通': 'Continue to membership',
  '前往会员中心加购': 'Continue to extra data',
  '会员充值': 'Membership',
  '流量包加速': 'Extra data',
  '选择会员或流量包，让加速继续。': 'Choose membership or extra data to keep accelerating.',
  '选择会员，享受高速加速。': 'Choose a membership for high-speed acceleration.',
  '随当前会员周期到期，不延长会员时长。': 'Expires with the current membership period; does not extend membership.',
  '选择会员或流量包': 'Choose a membership or data package',
  '随会员周期到期': 'Expires with membership',
  '{days} 天': '{days} days',
  '推荐': 'Recommended',
  '省 {percent}%': 'Save {percent}%',
  '暂无可购买会员，请稍后查看。': 'No memberships are available yet.',
  '暂无可购买流量包，请稍后查看。': 'No data packages are available yet.',
  '购买份数': 'Quantity',
  '每份 {traffic} GB': '{traffic} GB each',
  '应付金额': 'Total',
  '立即支付': 'Pay now',
  '支付方式暂未同步，请稍后重试。': 'Payment methods are not synced. Please try again.',
  '请先开通有效会员，再购买流量包。': 'Activate a membership before buying extra data.',
  '请选择 1–50 份。': 'Choose a quantity from 1 to 50.',
  '使用云流 App 加速': 'Connect with the Yunliu app',
  '点击加速后自动匹配正式节点，无需手动开通或选择授权槽位。': 'Tap Connect to be assigned a production node. No manual provisioning is required.',
  '请在 App 登录当前账户，点击加速即可申请连接。': 'Sign in to the app and tap Connect to request a connection.',
  '正式节点服务尚未开放，账户权益会保留。': 'Production nodes are not yet available. Your benefits are preserved.',
  '支付宝': 'Alipay',
  '微信支付': 'WeChat Pay',
  '支付方式': 'Payment method',
  '确认订单': 'Confirm order',
  '订单金额已更新，请确认最新金额后继续支付。': 'The order price has changed. Confirm the updated amount before continuing.',
  '支付完成': 'Payment complete',
  '订单': 'Order',
  '正在读取支付信息…': 'Loading payment details…',
  '无法读取支付信息，请关闭后重试。': 'Could not load payment details. Close and try again.',
  '无法连接支付服务，请查询订单后再试。': 'Could not connect. Check your order before trying again.',
  '支付申请未完成。': 'Payment request is incomplete.',
  '查询失败，请稍后重试。': 'Could not check the payment. Try again shortly.',
  '已确认到账，账户权益已更新。': 'Payment verified. Your account benefits have been updated.',
  '已收到付款，订单需要人工核对。请联系支持，不要重复支付。': 'Payment received; this order needs a review. Contact support and do not pay again.',
  '此订单由管理员确认到账，请联系支持。': 'This order requires manual confirmation. Contact support.',
  '在线支付尚未配置，请稍后再来。': 'Online payment is not configured yet. Please check back later.',
  '会员购买暂未开放，请稍后再来。': 'Membership purchases are not open yet. Please check back later.',
  '支付申请结果待核对，请先查询订单，不要重复付款。': 'The payment request needs verification. Check the order and do not pay again.',
  '将在安全收银台完成支付，到账结果由服务器核验。': 'Pay at the secure checkout. Our server verifies the result.',
  '已支付，查询结果': 'Check payment result',
  '继续支付': 'Continue payment',
  '前往支付': 'Go to checkout',
  '查看支付': 'View payment',
  '支付详情': 'Payment details',
  '付款待核对': 'Payment under review',
  '会员等级': 'Membership level',
  '当前网络带宽': 'Current bandwidth',
  '同时在线设备': 'Simultaneous devices',
  '带宽说明': 'Bandwidth information',
  '在线设备说明': 'Online device information',
  '上下行各 {speed} Mbps': '{speed} Mbps up / down',
  '{online} / {limit} 台': '{online} / {limit} devices',
  '账号共享上限，非实时测速。': 'Shared account cap, not a live speed test.',
  '账号共享上限，限速待节点生效。': 'Shared account cap; node enforcement is pending.',
  'App 加速时自动占用名额，断开后释放；连接中会预留名额，异常退出最多90秒回收。第三方客户端尚未计入。': 'The app automatically reserves a place when connecting and releases it after disconnecting. Unexpected exits expire within 90 seconds. Third-party clients are not counted yet.',
  '权益同步失败，请重试。': 'Could not sync account benefits. Please retry.',
  '重新同步': 'Retry sync',
  '待同步': 'Pending sync',
  '刷新开通进度': 'Refresh setup progress',
  '自动开通遇到问题，请联系支持；账户权益会保留。': 'Automatic setup needs attention. Contact support; your entitlements are saved.',
  '节点账号已创建，正在确认额度下发；确认后即可导入。': 'Your node account is created. Import will be available after quota delivery is confirmed.',
  '开通申请已保存，自动开户暂未启用，请联系支持。': 'Your request is saved. Automatic setup is not enabled yet; contact support.',
  '节点正在创建加速账号，请稍后刷新开通进度。': 'The node is creating your account. Refresh shortly to check progress.',
  '账户已进入自动开通队列，节点每 10 分钟处理一次，请稍后刷新。': 'Your account is queued. The node processes requests every 10 minutes; please refresh later.',
  '账户正在自动开通，奖励会保留，节点就绪后即可领取。': 'Account setup is in progress. Your rewards are saved; claim once the node is ready.',
  "邀请免费加速": "Invite & earn",
  "邀请好友注册，领取会员时长与高速流量。": "Invite friends to sign up and earn membership time and high-speed data.",
  "把云流分享给朋友，": "Share Yunliu with friends.",
  "下一段加速，我们请。": "Your next connection is on us.",
  "每邀请一位新好友完成注册，即可领取一份会员奖励。": "Earn a membership reward for each new friend who signs up.",
  "每位好友注册奖励": "Reward for each successful signup",
  "小时会员": "hours of membership",
  "高速流量": "high-speed data",
  "我的邀请码": "My invite code",
  "复制邀请码": "Copy invite code",
  "专属邀请链接": "Your invite link",
  "复制邀请链接": "Copy invite link",
  "好友打开链接后，邀请码会保存 30 天，注册时自动填入。": "The invite code is saved for 30 days and filled in when your friend signs up.",
  "有效邀请": "Valid invites",
  "待领取奖励": "Rewards to claim",
  "已领取会员时长": "Membership hours claimed",
  "邀请奖励记录": "Invite reward history",
  "还没有邀请记录。复制链接，邀请第一位朋友吧。": "No invites yet. Share your link with your first friend.",
  "好友注册奖励": "New friend signup reward",
  "生效时间": "Active period",
  "领取中…": "Claiming…",
  "领取奖励": "Claim reward",
  "已领取": "Claimed",
  "不可领取": "Unavailable",
  "奖励规则": "Reward rules",
  "已复制，分享给好友即可。": "Copied. Share it with a friend.",
  "无法访问剪贴板，请选中并复制邀请码或链接。": "Clipboard unavailable. Select and copy the code or link manually.",
  "已领取 24 小时会员和 3GB 高速流量。": "24 hours of membership and 3GB of high-speed data claimed.",
  "已领取权益，节点确认后可用，请刷新订阅。": "Reward claimed. Refresh your subscription after the node confirms delivery.",
  "尚未绑定加速节点，奖励会保留，请联系支持开通后领取。": "No node is linked yet. Your rewards are saved; contact support before claiming.",
  "节点权益控制尚未启用，奖励会保留，启用后即可领取。": "Node entitlement delivery is not enabled. Your rewards are saved until it is ready.",
  "权益正在同步到节点，通常在 10 分钟内更新。": "Your benefits are syncing to the node, usually within 10 minutes.",
  "节点已确认额度与有效期。": "The node has confirmed the quota and expiry.",
  "节点同步遇到问题，奖励会保留，请联系支持。": "Node sync needs attention. Your unclaimed rewards are saved; contact support.",
  "节点暂时离线，奖励会保留，恢复后再领取。": "The node is offline. Your unclaimed rewards are saved; claim after it recovers.",
  "会员速度上限尚未在节点验证，不作为已生效承诺。": "Member speed limits are not yet verified on the node.",
  "奖励已保留，节点尚未就绪，请先联系支持开通或恢复节点后领取。": "Your reward is saved. Contact support to enable or restore the node before claiming.",
  "正在加载邀请记录…": "Loading invite history…",
  "邀请服务暂不可用，请重试。": "Invites are temporarily unavailable. Please retry.",
  "奖励领取失败，请重试。": "Could not claim the reward. Please retry.",
  "奖励发给邀请人，每位新好友仅计一次；邀请码需在注册时填写，注册后不能补填或更换。": "Rewards go to the inviter, once per new friend. Enter the invite code at signup; it cannot be added or changed later.",
  "奖励领取后生效；已有会员按最后一个会员周期接续，3GB 随对应的 24 小时奖励周期到期。": "Claim to start your reward. Existing membership is extended after its last cycle; the 3GB expires with the matching 24-hour reward cycle.",
  "同一设备仅允许注册一个新账户，同网络每天最多注册 5 个；重复注册、自己邀请自己不能获得奖励。": "One new account per device and up to 5 signups per public network daily. Duplicate signups and self-referrals do not earn rewards.",
  "本页为会员权益账本；节点实时流量与连接状态以订阅管理为准。": "This is your entitlement ledger. Live node usage and connection status are shown in Subscription.",
  "好友邀请码（选填）": "Friend’s invite code (optional)",
  "输入邀请码或粘贴邀请链接": "Enter a code or paste an invite link",
  "粘贴并保存邀请码": "Paste and save invite code",
  "已记住邀请码，注册成功后自动清除。": "Invite code saved. Cleared after successful signup.",
  "剪贴板中没有有效的邀请码或邀请链接。": "No valid code or invite link found in the clipboard.",
  "邀请码已保存，注册时会自动使用。": "Invite code saved. It will be used at signup.",
  "请手动粘贴邀请码，浏览器未允许读取剪贴板。": "Paste the code manually; clipboard access was denied.",
  "邀请码无效，请检查后重试。": "Invalid invite code. Please check and retry.",
  "邀请奖励会员": "Invite reward membership",
  "每位好友 · 24 小时 + 3GB": "24 hours + 3GB per friend",
  "会员权益累计至": "Membership scheduled until",
  "此设备已注册过账户，请登录已有账户。": "An account has already been registered on this device. Sign in instead.",
  "当前网络注册过于频繁，请明天再试。": "Signup limit reached for this network. Try again tomorrow.",
  "请完成设备验证后注册。": "Complete device verification before signup.",
  "注册操作过于频繁，请明天再试。": "Too many signup attempts. Try again tomorrow.",
  "请使用新版客户端或浏览器完成设备验证。": "Use an updated app or browser to verify your device.",
  "奖励当前不可领取，请联系支持。": "This reward cannot be claimed. Contact support.",
  "注册验证暂不可用，请稍后重试。": "Signup verification is temporarily unavailable. Try again later.",
  "邀请好友，免费加速": "Invite friends, earn free acceleration",
  "每位新好友完成注册，你都能领取一份会员奖励。": "Claim a membership reward for each new friend who signs up.",
  "把云流分享给朋友": "Share Yunliu with friends",
  "GB 高速流量": "GB of high-speed data",
  "好友在 App 注册时填写邀请码，或通过你的官网链接注册。": "Friends can enter your code in the app or sign up using your website link.",
  "演示模式不生成真实邀请码，也不会发放奖励。": "Demo mode does not generate real invite codes or grant rewards.",
  "登录并获取邀请码": "Sign in to get your invite code",
  "获取邀请码": "Get invite code",
  "每位新好友仅计一次，奖励发给邀请人，注册后不能补填或更换邀请码。": "Each new friend counts once. The inviter earns the reward; codes cannot be added or changed after signup.",
  "领取后增加 24 小时会员；已有会员从最后一个周期到期后接续，3GB 随对应奖励周期到期。": "Claim 24 hours of membership. Rewards follow any existing cycles, and 3GB expires with the matching reward cycle.",
  "同一设备仅允许注册一个新账户，同网络每天最多注册 5 个；重复注册和自邀不发奖。": "One new account per device and up to 5 daily signups per public network. No rewards for duplicate signups or self-referrals.",
  "此页为会员权益账本，节点实时流量与连接状态以账户订阅为准。": "This is your entitlement ledger; live node usage and connection status come from your subscription.",
  "注册后不能补填或更换邀请码。": "Invite codes cannot be added or changed after signup.",
  "无法完成设备验证，请使用新版客户端或官网注册。": "Device verification failed. Use an updated app or sign up on the website.",
  "奖励当前不可领取，请刷新记录或联系支持。": "This reward cannot be claimed. Refresh or contact support.",
  "套餐流量、速度上限与有效期一目了然。": "Data allowances, speed caps and validity are clear up front.",
  "基础月度会员": "Basic monthly membership",
  "高速流量加油包": "High-speed data top-up",
  "每个会员周期包含 100GB 高速流量": "100GB of high-speed data per membership cycle",
  "20GB 起充，仅有效会员可购买，随当前会员周期到期": "Top up from 20GB with an active membership. Expires with the current membership cycle.",
  "每周期 100GB 高速流量": "100GB of high-speed data per cycle",
  "账号合计上限 20Mbps": "20Mbps speed cap shared across the account",
  "会员可加购高速流量": "Members can top up high-speed data",
  "有效期 30 天": "Valid for 30 days",
  "每份 20GB 高速流量": "20GB of high-speed data per pack",
  "仅有效会员可购买": "Available to active members",
  "随当前会员周期到期": "Expires with the current membership cycle",
  "不延长会员时间": "Does not extend membership",
  "仅有效会员可购买，随当前会员周期到期": "Requires an active membership. Expires with the current cycle.",
  "开通会员": "Become a member",
  "会员加购": "Member top-up",
  "每份": "per pack",
  "低速体验": "Basic trial",
  "高级会员": "Premium member",
  "普通会员": "Ordinary member",
  "授权槽位": "Authorized slots",
  "普通会员1个，高级会员3个；每个槽位独立开通连接凭据。": "1 slot for ordinary members, 3 for premium members. Each slot receives independent connection credentials.",
  "授权槽位不是严格物理设备限制。共享入口执行层仍待验收，新槽位暂不能导入客户端；现有测试订阅保留。": "Slots are not strict physical-device limits. Shared-entry enforcement is pending validation; new slots cannot be imported yet. Existing test subscriptions are preserved.",
  "已分配": "Assigned",
  "等待节点撤销确认": "Waiting for node revocation confirmation",
  "超出当前等级，待回收": "Above current tier; awaiting reclamation",
  "待节点开通": "Pending node provisioning",
  "撤销授权": "Revoke authorization",
  "槽位名称": "Slot name",
  "例如：我的手机": "For example: my phone",
  "创建槽位": "Create slot",
  "可以先创建授权槽位，节点执行层上线后再开通。": "Create a slot now; connection credentials will be provisioned when node enforcement is ready.",
  "账号峰值上限": "Account peak limit",
  "上传与下载分别按账号合计限速，所有槽位和节点共享各自上限。": "Upload and download are limited separately per account. All slots and nodes share each directional limit.",
  "授权槽位上限": "Authorized credential slots",
  "限速目标待节点验收，尚不代表实际已限速。": "The target rate is pending node verification, not an enforced speed limit.",
  "高速额度已用完，当前会员周期停止加速，不使用免费额度。": "Premium quota exhausted. Acceleration stops until replenished; free quota is not used during this cycle.",
  "免费额度已用完，次月北京时间零点恢复。": "Free quota exhausted. It resets at midnight Beijing time on the first day of next month.",
  "付费会员每30天发放100GB，未用完不结转；有效期以所购套餐为准，续费排队生效。": "Paid membership provides 100GB every 30 days without rollover. Validity follows your plan; renewals start after the current cycle.",
  "邀请会员每份24小时与3GB，按奖励周期生效。": "Each invitation reward includes 24 hours and 3GB, valid during its reward cycle.",
  "当前连接仍使用原测试基线额度；本月免费钱包尚未接入该节点，实际可用流量以订阅管理为准。": "This connection still uses its original test allowance. The monthly free wallet is not connected to this node yet; see Subscriptions for usable data.",
  "免费额度5GB，按北京自然月重置。": "5GB free allowance, renewed each Beijing calendar month.",
  "高速剩余流量": "High-speed data remaining",
  "会员有效期至": "Membership expires",
  "本月免费体验剩余": "Free trial data remaining this month",
  "免费额度重置时间": "Free data resets",
  "下个会员周期开始时间": "Next membership cycle starts",
  " / 份，随会员周期到期": " / pack, expires with the membership cycle",
  "请先开通会员": "Membership required",
  "加油包份数": "Number of top-up packs",
  "每份 20GB；仅有效会员可购买，不延长会员时间。": "20GB per pack. Active members only; does not extend membership.",
  "加油包到期时间": "Top-up expires",
  "，随会员周期到期": ", expires with the membership cycle",
  "该套餐暂未开放购买，请稍后再试。": "This plan is not available for purchase yet. Please try again later.",
  "请先开通有效会员，再购买高速流量加油包。": "An active membership is required to top up high-speed data.",
  "用户协议": "Terms of Service",
  "隐私政策": "Privacy Policy",
  "我已阅读并同意": "I have read and agree to",
  "请先阅读并同意用户协议和隐私政策。": "Please read and agree to the Terms and Privacy Policy.",
  "和": "and",

  "云流加速器首页": "Yunliu home",
  "Flux cloud": "Flux cloud",
  "云流": "Yunliu",
  "加速器": "VPN",
  "账户中心": "Account",
  "首页": "Home",
  "套餐价格": "Pricing",
  "下载客户端": "Downloads",
  "跳到主要内容": "Skip to main content",
  "云流首批体验名单现已开放": "Yunliu early access is now open",
  "注册后免费加速": "Free acceleration after registration",
  "立即加入": "Join now",
  "立即体验": "Try it now",
  "免费体验": "Try for free",
  "免费注册": "Sign up free",
  "主导航": "Main navigation",
  "登录": "Log in",
  "加入体验": "Join early access",
  "关闭菜单": "Close menu",
  "打开菜单": "Open menu",
  "切换到英文": "Switch to English",
  "切换到中文": "Switch to Chinese",
  "移动端导航": "Mobile navigation",
  "登录账户": "Log in",
  "加入首批体验": "Join early access",
  "让海外华人简单、稳定地连接熟悉的国内内容。": "A simple, reliable way for people overseas to stay connected to the content they know from China.",
  "产品": "Product",
  "了解我们": "About",
  "联系支持": "Contact support",
  "AI 内容说明": "AI content policy",
  "站点地图": "Sitemap",
  "服务状态": "Service status",
  "官网与账户服务正常": "Website and account services are operational",
  "支持 Windows、macOS、iOS 与 Android。": "Available for Windows, macOS, iOS, and Android.",
  "智能分流 · 多端可用 · 线路透明": "Smart routing · Multiple devices · Clear routing",
  "云流跨山海": "Crossing mountains and seas,",
  "一键连所爱": "connecting all you love in one click.",
  "所爱隔山海": "Separated by mountains and seas,",
  "一秒解乡愁": "connected to home in a heartbeat.",
  "海外访问国内内容": "Access the content you love in China",
  "一个云流就够了": "One connection is all you need",
  "注册即享免费体验额度，随时连接熟悉的国内影音与游戏。": "Sign up to get free trial data, connecting you to videos, music, and games back home.",
  "免费获取通知": "Get notified",
  "国内影音、音乐、国服游戏，一次连接。智能分流只加速需要回国的流量，不打扰你的海外网络。": "Connect to video, music, and game services in China. Smart routing accelerates only the traffic that needs it, while your local internet stays on its usual path.",
  "查看套餐": "View plans",
  "云流桌面客户端界面预览": "Yunliu desktop app preview",
  "云流加速器全球连接中国网络示意图": "Illustration of Yunliu connecting users worldwide to China",
  "全球节点连接中国网络示意图": "Illustration of global nodes connecting to China",
  "云流客户端": "Yunliu app",
  "客户端界面预览": "App preview",
  "客户端功能导航": "App navigation",
  "总览": "Overview",
  "分流": "Routing",
  "设备": "Devices",
  "设置": "Settings",
  "连接总览": "Connection overview",
  "晚上好，准备开始加速": "Ready to connect?",
  "智能模式": "Smart mode",
  "已加速": "Connected",
  "智能线路 · 中国": "Smart route · China",
  "连接状态": "Connection status",
  "连接正常": "Connected",
  "当前模式": "Current mode",
  "仅处理国内应用": "China-bound apps only",
  "加速线路": "Accelerated route",
  "自动选择": "Automatic",
  "按网络状态匹配": "Selected for your network",
  "腾讯视频": "Tencent Video",
  "影音线路": "Video route",
  "网易云音乐": "NetEase Cloud Music",
  "音乐线路": "Music route",
  "优酷": "Youku",
  "QQ音乐": "QQ Music",
  "海外网站": "International websites",
  "本地直连": "Direct connection",
  "影音 · 音乐 · 游戏": "Video · Music · Games",
  "覆盖常用回国场景": "For the services you use most",
  "智能分流": "Smart routing",
  "海外应用不绕路": "Keep local traffic local",
  "多平台客户端": "Multi-platform apps",
  "多端同步": "Sync across devices",
  "一个账户连接常用设备": "One account for your devices",
  "熟悉的国内内容，在海外继续使用": "Keep using the services you know, wherever you are",
  "把复杂的网络选择，留在产品背后": "A simpler way to reach the content you want",
  "上手简单": "Easy to use",
  "下载安装、登录账户、选择场景。线路匹配和连接维护交给云流。": "Install the app, sign in, and choose a use case. Yunliu takes care of routing and connection maintenance.",
  "分流精准": "Precise routing",
  "只处理需要回国访问的流量，让海外网站和日常工作保持原有路径。": "Only China-bound traffic is routed through Yunliu. Your international sites and work apps stay on their usual path.",
  "设备全面": "Your devices, together",
  "规划覆盖 Windows、macOS、iOS 与 Android，一个账户同步常用设备。": "Apps are planned for Windows, macOS, iOS, and Android, with your devices managed from one account.",
  "安全连接": "Secure connection",
  "账户与会话保护": "Account and session protection",
  "四端规划": "Four platforms planned",
  "桌面与移动平台": "Desktop and mobile",
  "智能线路": "Smart routes",
  "按场景自动匹配": "Matched to your use case",
  "了解云流加速器": "About Yunliu",
  "产品定位、支持设备、智能分流与套餐使用方式，都在这里。": "Learn how Yunliu works, which devices are supported, how smart routing behaves, and how plans are used.",
  "还有问题？联系支持": "Questions? Contact support",
  "把熟悉的内容，重新连接起来": "Reconnect with the content you know",
  "加入首批体验名单，客户端开放下载时第一时间通知你。": "Join the early access list and get notified when the apps are ready.",
  "国内影音": "China video services",
  "喜欢的节目，不再受距离限制": "Your favorite shows, wherever you are",
  "针对常用国内视频平台匹配回国线路，减少等待、卡顿和反复切换节点。": "Yunliu selects routes for popular video services in China to reduce buffering and manual server switching.",
  "覆盖常用国内影音平台": "Popular video services in China",
  "根据网络状态自动选择线路": "Routes selected for your network",
  "高清视频播放更连贯": "Smoother HD playback",
  "音乐畅听": "Music",
  "歌单、电台和播客随时继续": "Pick up where your playlists left off",
  "只处理需要回国访问的音乐流量，日常浏览和工作连接保持原有路径。": "Only music traffic that needs a China route is accelerated. Browsing and work connections stay on their usual path.",
  "支持主流国内音乐服务": "Popular music services in China",
  "后台自动保持连接": "Connection maintained in the background",
  "无需频繁切换网络模式": "No need to keep switching modes",
  "国服游戏": "Games in China",
  "稳定连接熟悉的国服世界": "A steadier connection to your game servers",
  "面向国服连接优化传输路径，降低跨境网络带来的延迟波动。": "Routes are optimized for game servers in China to help reduce latency swings across borders.",
  "按游戏场景选择线路": "Routes chosen for gaming",
  "实时显示延迟状态": "Latency status at a glance",
  "异常后自动尝试恢复": "Automatic recovery attempts",
  "国内应用加速，海外应用不绕路": "Accelerate China-bound apps, keep everything else local",
  "云流自动识别目标应用，仅将需要回国的流量送入加速线路。": "Yunliu identifies supported apps and routes only the traffic that needs a connection to China.",
  "国内应用走回国线路": "China-bound apps use Yunliu routes",
  "海外服务保持本地直连": "International services connect directly",
  "智能模式与手动模式可切换": "Switch between smart and manual modes",
  "一个账户，连接常用设备": "One account for your devices",
  "电脑和手机共享账户、套餐与连接状态，换设备无需重新配置。": "Your account, plan, and connection status stay in sync across computers and phones.",
  "覆盖桌面与移动平台": "Desktop and mobile devices",
  "套餐权益保持同步": "Plan benefits stay in sync",
  "统一查看设备状态": "Manage device status in one place",
  "爱奇艺": "iQIYI",
  "哔哩哔哩": "Bilibili",
  "QQ 音乐": "QQ Music",
  "播客与电台": "Podcasts and radio",
  "低延迟线路": "Low-latency route",
  "语音服务": "Voice services",
  "游戏更新": "Game updates",
  "回国加速": "China route",
  "视频会议": "Video calls",
  "当前设备": "This device",
  "账户已同步": "Account synced",
  "移动设备": "Mobile device",
  "等待连接": "Waiting to connect",
  "已连接至中国线路": "Connected to a route in China",
  "你的位置": "Your location",
  "高速线路": "High-speed route",
  "已就绪": "Ready",
  "一次连接，覆盖常用回国场景": "One connection for the services you use most",
  "选择场景，云流自动处理线路和分流规则。": "Choose a use case. Yunliu handles routing and traffic rules.",
  "产品能力": "Product features",
  "产品特性": "Product highlights",
  "需要加速的才加速": "Accelerate only what needs it",
  "开启智能分流后，国内内容进入云流线路；海外网站、工作软件和视频会议继续使用本地网络。": "With smart routing on, traffic to services in China uses Yunliu. International websites, work apps, and video calls stay on your local connection.",
  "线路模式": "Routing mode",
  "普通 VPN": "Standard VPN",
  "云流智能分流": "Yunliu smart routing",
  "线路状态": "Route status",
  "智能模式已开启": "Smart mode is on",
  "全局代理模式": "Full-tunnel mode",
  "你的设备": "Your device",
  "代理线路": "VPN route",
  "更短的连接路径": "A more direct route",
  "所有流量统一绕行": "All traffic takes the VPN route",
  "减少不必要的线路占用": "Less unnecessary routing",
  "海外服务也会进入代理线路": "International services also use the VPN route",
  "提交失败，请稍后重试。": "Could not submit. Please try again later.",
  "提交成功！": "Submitted successfully.",
  "邮箱地址": "Email address",
  "输入邮箱，获取开放测试通知": "Enter your email for early access updates",
  "提交中…": "Submitting…",
  "加入体验名单": "Join the early access list",
  "选择适合你的流量套餐": "Choose a plan that fits your usage",
  "按流量计费，不限速。套餐流量与有效期一目了然。": "Pay for data with no speed cap. Data allowances and validity are clear up front.",
  "月度": "Monthly",
  "季度": "Quarterly",
  "年度": "Yearly",
  "自定义": "Custom",
  "原价": "Was",
  "有效期 {days} 天": "Valid for {days} days",
  "按流量计费，不限速，不浪费。所有套餐自启用之日起 30 天有效。": "Pay for data, with no speed cap. Every plan is valid for 30 days from activation.",
  "高速国内节点": "High-speed routes in China",
  "智能应用分流": "Smart app routing",
  "全平台客户端": "Apps across platforms",
  "清晰使用记录": "Clear usage history",
  "选择套餐": "Choose plan",
  "套餐和价格已与线上系统实时同步。": "Plans and prices are synced with the live service.",
  "正在使用缓存套餐信息，最终以账户中心为准。": "Showing cached plan information. Your account center has the latest details.",
  "购买前需要了解": "Before you buy",
  "流量、有效期和设备使用情况都会在账户中心清晰展示。": "Your data, validity, and device usage are shown in your account center.",
  "套餐用完后怎么办？": "What happens when I use all my data?",
  "可以随时购买新的套餐。新套餐的流量和有效期将按购买规则生效。": "You can buy another plan at any time. Its data and validity follow the purchase terms.",
  "可以在多台设备使用吗？": "Can I use Yunliu on multiple devices?",
  "可以。账户支持管理多台设备，同时在线数量会在套餐详情中说明。": "Yes. Your account can manage multiple devices. The plan details show how many can be online at once.",
  "海外华人回国加速器": "China Accelerator for Overseas Users",
  "独创边缘节点加速技术，畅享国内网络": "Proprietary edge acceleration technology for seamless access to Chinese networks",
  "官方客户端": "Official Clients",
  "Flux cloud 电脑端": "Flux cloud Desktop",
  "Flux cloud 手机端": "Flux cloud Mobile",
  "Flux cloud 电视端": "Flux cloud TV",
  "Windows 版下载": "Download for Windows",
  "Mac 版下载": "Download for Mac",
  "Android 版下载": "Download for Android",
  "iOS 版下载": "Download for iOS",
  "TV 公测版下载": "Download TV Beta",
  "支持 Android TV 与智能盒子": "Supports Android TV and smart TV boxes",
  "TV 公测版申请": "TV Beta Access",
  "TV 公测版安装包正在灰度分发中，可前往账户中心提交工单或联系客服获取内测资格。": "The TV beta package is in rollout testing. Contact support or open a ticket in account center to get early beta access.",
  "我知道了": "Got it",
  "客户端下载｜Flux cloud (云流)": "Client Downloads | Flux cloud",
  "Flux cloud 官方客户端下载，支持 Windows、macOS、iOS、Android 与 TV 全平台。独创边缘节点加速，畅享国内网络。": "Flux cloud official app downloads for Windows, macOS, iOS, Android, and TV. Accelerate connections to China with edge routing.",
  "影音模式": "Streaming Mode",
  "已连接 · 极速专线": "Connected · Express Route",
  "回国专线 · 极速模式": "China Route · High Speed",
  "一键加速 · 已连接": "One-click Speedup · Connected",
  "公测专版": "Public Beta Edition",
  "一键加速": "Speed Up",
  "家庭大屏 4K": "Big Screen 4K",
  "家庭大屏 4K 超清影音体验": "Big screen 4K ultra HD entertainment",
  "线路切换": "Route Switch",
  "4K 超清大屏就绪": "4K Ultra HD Ready",
  "安装 Clash，一键导入云流": "Install Clash and import Yunliu in one click",
  "当前通过开源客户端连接。Windows、macOS 与 Linux 可使用 Clash Verge Rev；先安装兼容内核，再到账户中心导入私人订阅。": "Yunliu currently connects through an open-source client. Clash Verge Rev is available for Windows, macOS, and Linux. Install a compatible core, then import your private subscription from the account center.",
  "Clash Verge Rev 官方下载": "Download Clash Verge Rev",
  "前往账户中心": "Go to account center",
  "Windows 10 及以上": "Windows 10 or later",
  "macOS 12 及以上": "macOS 12 or later",
  "iOS 15 及以上": "iOS 15 or later",
  "Android 10 及以上": "Android 10 or later",
  "即将开放": "Coming soon",
  "准备中": "In progress",
  "下载 v{version}": "Download v{version}",
  "三步导入私人订阅": "Import your private subscription in three steps",
  "安装新版 Clash Verge Rev，确认 Mihomo 内核兼容（已验证 1.19.29，1.18.7 需升级）": "Install the latest Clash Verge Rev and confirm the Mihomo core is compatible (1.19.29 verified; upgrade from 1.18.7).",
  "登录云流账户，在私人订阅中点击“一键导入 Clash”，允许浏览器打开客户端": "Sign in to Yunliu, select “Import to Clash” under Private subscription, and allow your browser to open the app.",
  "选中云流订阅，使用规则模式并开启系统代理；未弹出时复制链接到“订阅”页面导入": "Select the Yunliu subscription, use Rule mode, and enable the system proxy. If the app does not open, copy the link and import it on the Subscriptions page.",
  "导入不等于连接成功。当前配置仅支持已验收的 TCP 影音场景，UDP / 游戏加速尚未验收；手机端需使用支持相同 REALITY 参数的客户端。": "Importing a profile does not guarantee a working connection. The current configuration is verified for TCP video and music use; UDP and gaming are not yet verified. Mobile clients must support the same REALITY parameters.",
  "自研客户端开放通知": "Get notified when Yunliu apps are ready",
  "留下邮箱即可，不会向你发送无关推广信息。": "Leave your email. We will not send unrelated promotions.",
  "这个页面暂时找不到": "This page could not be found",
  "链接可能已经变化，返回首页继续了解云流加速器。": "This link may have changed. Visit the home page to learn more about Yunliu.",
  "返回首页": "Back to home",
  "云流加速器｜海外访问国内影音、音乐与国服游戏": "Yunliu | Access video, music, and games in China",
  "Clash 客户端下载与一键导入｜云流": "Clash download and one-click import | Yunliu",
  "账户中心｜套餐、订单与流量权益｜云流": "Account center | Plans, orders, and data | Yunliu",
  "云流是什么？": "What is Yunliu?",
  "云流是一款面向海外华人的回国加速器，用于改善访问国内影音、音乐和国服游戏时的网络连接体验。": "Yunliu helps people overseas connect to video, music, and game services in China with a more reliable network experience.",
  "云流支持哪些设备？": "Which devices does Yunliu support?",
  "规划支持 Windows、macOS、iOS 和 Android。一个账户可以管理多台设备，具体同时在线数量以所选套餐为准。": "Apps are planned for Windows, macOS, iOS, and Android. One account can manage multiple devices; simultaneous connections depend on your plan.",
  "它会影响海外网站的访问速度吗？": "Will Yunliu affect access to international websites?",
  "客户端采用智能分流，只将需要回国加速的流量发送到国内线路，其他海外网站和应用保持原有网络路径。": "Smart routing sends only China-bound traffic through Yunliu. International websites and apps continue using their usual network route.",
  "可以加速腾讯视频、爱奇艺和国服游戏吗？": "Can Yunliu accelerate Tencent Video, iQIYI, and games in China?",
  "产品将覆盖腾讯视频、爱奇艺、优酷、哔哩哔哩、QQ音乐、网易云音乐以及常见国服游戏。实际支持范围会随线路测试持续更新。": "Yunliu is designed for Tencent Video, iQIYI, Youku, Bilibili, QQ Music, NetEase Cloud Music, and popular games in China. Supported services may change as routes are tested.",
  "套餐流量从什么时候开始计算？": "When does my plan's validity begin?",
  "套餐在首次启用后进入有效期。我们会在购买页和账户中心清晰展示流量、有效期与使用记录。": "A plan's validity starts when it is first activated. Your data, validity, and usage history are shown in the purchase flow and account center.",
  "云流加速器面向海外华人，智能加速国内影音、音乐与国服游戏，仅处理需要回国的流量。": "Yunliu helps people overseas access video, music, and game services in China. Smart routing handles only the traffic that needs a route to China.",
  "云流提供简单透明的按流量套餐，30GB起，套餐有效期30天，支持智能分流与多平台客户端。": "Yunliu offers straightforward data plans starting at 30 GB, valid for 30 days, with smart routing across multiple platforms.",
  "安装开源 Clash Verge Rev，登录云流账户，一键导入私人订阅。查看兼容内核、规则模式与手动导入步骤。": "Install the open-source Clash Verge Rev app, sign in to Yunliu, and import your private subscription. See compatible cores, Rule mode, and manual import steps.",
  "登录云流账户，一键导入 Clash 订阅，查看节点实测流量、有效期、套餐权益与订单。": "Sign in to Yunliu to import your Clash subscription and review measured node usage, validity, plan benefits, and orders.",
  "云流加速器,云流VPN,回国加速器,海外看国内视频,国服游戏加速,海外华人": "Yunliu, Yunliu VPN, China VPN, watch Chinese video overseas, China game acceleration",
  "回国加速器价格,云流VPN套餐,100GB套餐,海外华人加速器": "China VPN pricing, Yunliu plans, 100 GB plan, VPN for people overseas",
  "云流VPN下载,回国加速器客户端,Windows加速器,Mac加速器": "Yunliu download, China VPN app, Windows VPN, Mac VPN",
  "云流VPN账户,流量套餐,订单查询": "Yunliu account, data plans, order history",
  "账户总览": "Account overview",
  "订阅管理": "Subscription",
  "套餐与续费": "Plans and billing",
  "订单记录": "Order history",
  "个人设置": "Personal settings",
  "账户权益、个人信息与常用入口。": "Your plan, profile, and shortcuts.",
  "查看连接状态、流量信息，并安全管理你的订阅配置。": "Check connection status and usage, and manage your subscription securely.",
  "根据使用习惯选择合适的流量额度。": "Choose a data plan that fits your usage.",
  "查看套餐订单的金额、状态与创建时间。": "Review order amounts, statuses, and dates.",
  "登录或注册": "Log in or sign up",
  "关闭账户菜单": "Close account menu",
  "打开账户菜单": "Open account menu",
  "云流账户": "Yunliu account",
  "登录与注册": "Log in or sign up",
  "客户端下载": "Download apps",
  "返回官网": "Back to website",
  "正在加载账户信息": "Loading account information",
  "让熟悉的内容，": "Stay connected to the content you know",
  "随时与你相连。": "wherever you are.",
  "登录后管理订阅、流量权益和订单，也可以在这里完成客户端配置。": "Sign in to manage subscriptions, data, and orders, or set up your app.",
  "订阅与套餐集中管理": "Manage subscriptions and plans",
  "订单状态随时查看": "Check order status anytime",
  "账户信息由你掌控": "You control your account information",
  "创建账户": "Create account",
  "欢迎回来": "Welcome back",
  "创建云流账户": "Create your Yunliu account",
  "使用你的邮箱和密码继续。": "Sign in with your email and password.",
  "注册后即可管理个人订阅。": "Manage your subscriptions after signing up.",
  "昵称": "Display name",
  "如何称呼你": "What should we call you?",
  "邮箱": "Email",
  "密码": "Password",
  "至少 8 位": "At least 8 characters",
  "无法连接账户服务，请检查网络后重试。": "Could not connect to the account service. Check your network and try again.",
  "无法连接账户服务，请稍后重试。": "Could not connect to the account service. Please try again later.",
  "邮箱或密码不正确。": "The email or password is incorrect.",
  "该邮箱已经注册。": "This email is already registered.",
  "注册暂未开放。": "Sign-up is not available yet.",
  "账号当前不可用。": "This account is currently unavailable.",
  "请输入有效的邮箱地址。": "Enter a valid email address.",
  "密码长度需为 8–128 位。": "Password must be 8–128 characters long.",
  "套餐不存在或已下架。": "This plan is unavailable or has been removed.",
  "请先登录。": "Please sign in first.",
  "请填写邮箱。": "Enter your email address.",
  "请填写密码。": "Enter your password.",
  "请填写昵称。": "Enter a display name.",
  "JSON 格式不正确。": "The JSON format is invalid.",
  "请求内容必须是 JSON 对象。": "The request body must be a JSON object.",
  "请使用 application/json 提交。": "Submit using application/json.",
  "请求内容不能超过 32KB。": "The request body cannot exceed 32 KB.",
  "订阅未开通、已到期、额度用完或节点状态待同步。请登录账户中心查看。": "Your subscription may not be active, may have expired or run out of data, or may be waiting for a node sync. Sign in to your account center for details.",
  "保存失败，请检查网络后重试。": "Could not save. Check your connection and try again.",
  "退出登录失败，请稍后重试。": "Could not sign out. Please try again later.",
  "邮箱格式不正确。": "The email address is invalid.",
  "订单已创建，请等待支付渠道接入或联系管理员确认。": "Your order was created. Wait for payment options to become available or contact support.",
  "提交成功，我们会在产品开放测试时联系你。": "Thanks! We will contact you when the product opens for testing.",
  "请求失败（HTTP {status}）": "Request failed (HTTP {status})",
  "处理中…": "Working…",
  "账户连接使用加密会话保护": "Your account is protected by an encrypted session",
  "账户摘要": "Account summary",
  "常用功能": "Shortcuts",
  "账户设置": "Account settings",
  "管理个人资料与当前套餐信息。": "Manage your profile and current plan details.",
  "关闭账户设置": "Close account settings",
  "账户设置分类": "Account settings sections",
  "关闭": "Close",
  "套餐与流量": "Plan and data",
  "当前套餐": "Current plan",
  "账户资料": "Account profile",
  "填写你希望使用的称呼": "Enter the name you would like to use",
  "账户导航": "Account navigation",
  "账户功能": "Account features",
  "云流导航": "Yunliu navigation",
  " / {days}天": " / {days} days",
  "你好，{name}": "Hello, {name}",
  "云流用户": "Yunliu user",
  "刷新账户信息": "Refresh account information",
  "正在退出": "Signing out…",
  "退出登录": "Sign out",
  "云流账户权益": "Yunliu account benefits",
  "可选套餐": "Available plans",
  "当前生效的流量权益": "Your active data benefits",
  "当前暂无生效套餐，{count} 款套餐可选": "No active plan. {count} plans are available.",
  "暂无生效套餐": "No active plan",
  "生效中": "Active",
  "可选择": "Available",
  "未开通": "Not subscribed",
  "账户剩余流量": "Data remaining",
  "本套餐额度": "Plan allowance",
  "有效期至": "Valid until",
  "套餐已用流量比例": "Plan data used",
  "查看套餐与续费": "View plans and billing",
  "编辑资料": "Edit profile",
  "个人信息": "Personal information",
  "登录邮箱": "Sign-in email",
  "账户状态": "Account status",
  "正常": "Active",
  "暂不可用": "Unavailable",
  "管理连接配置与订阅链接": "Manage connection settings and subscription links",
  "查看支付与订单状态": "Review payment and order status",
  "当前有 {count} 款套餐可选，创建订单后可在订单记录中查看。": "There are {count} plans available. Orders appear in your order history.",
  "查看全部": "View all",
  "创建订单": "Create order",
  "订阅配置": "Subscription settings",
  "节点实测数据": "Measured node data",
  "按流量计费 · 有效期清晰": "Data-based pricing · Clear validity",
  "共 {count} 笔订单，最近 100 笔会显示在这里。": "You have {count} orders. Your 100 most recent orders are shown here.",
  "刷新记录": "Refresh orders",
  "订单编号": "Order number",
  "套餐": "Plan",
  "金额": "Amount",
  "状态": "Status",
  "创建时间": "Created",
  "待支付": "Pending",
  "已支付": "Paid",
  "已取消": "Cancelled",
  "已退款": "Refunded",
  "还没有订单": "No orders yet",
  "选择套餐后，订单会显示在这里。": "Orders will appear here after you choose a plan.",
  "查看可选套餐": "Browse plans",
  "个人资料": "Profile",
  "这些信息用于识别你的云流账户。": "This information identifies your Yunliu account.",
  "不可修改": "Cannot be changed",
  "个人资料已保存": "Profile saved",
  "保存中…": "Saving…",
  "保存资料": "Save profile",
  "暂无有效套餐": "No active plan",
  "剩余流量": "Data remaining",
  "套餐额度": "Plan allowance",
  "节点订阅的实时用量以“订阅管理”中的同步信息为准。": "For live node usage, see the latest sync in Subscription.",
  "加载": "Loading",
  "订阅暂时不可用。": "Subscription is temporarily unavailable.",
  "重置失败。": "Could not reset the subscription.",
  "可用": "Available",
  "待开通": "Pending activation",
  "已到期": "Expired",
  "流量已用完": "Data used up",
  "已停用": "Disabled",
  "等待节点同步": "Waiting for node sync",
  "不限流量": "Unlimited",
  "未设置到期时间": "No expiration set",
  "私人订阅": "Private subscription",
  "一键导入 Clash": "Import to Clash",
  "一键导入 (iOS)": "One-click import (iOS)",
  "使用客户端连接云流，无需逐项填写节点配置。": "Connect to Flux cloud with your client—no manual node setup required.",
  "使用开源客户端连接云流，无需逐项填写节点配置。": "Connect to Yunliu with an open-source app—no need to configure nodes manually.",
  "多平台客户端下载": "Multi-platform Client Downloads",
  "选择与你的设备匹配的客户端，支持主流桌面端与移动端。": "Choose the client matching your device, supporting major desktop and mobile platforms.",
  "适用于 Windows 10 / 11": "For Windows 10 / 11",
  "适用于 macOS 12+": "For macOS 12+",
  "适用于 iPhone / iPad": "For iPhone / iPad",
  "适用于 Android 10+": "For Android 10+",
  "App Store 下载": "Download on App Store",
  "节点订阅套餐": "Node subscription plan",
  "暂未绑定节点客户端，请联系管理员开通。": "No node client linked yet. Please contact the administrator.",
  "请在浏览器弹窗中允许打开客户端。导入后选中云流订阅，开启系统代理；未弹出时可复制链接手动导入。": "Please allow opening the client in the browser prompt. Once imported, select your Flux subscription and enable system proxy.",
  "刷新订阅": "Refresh subscription",
  "正在获取订阅…": "Loading subscription…",
  "节点已用流量": "Node data used",
  "节点总额度": "Node allowance",
  "有效期": "Validity",
  "节点已用流量比例": "Node data used",
  "暂未绑定节点客户端。套餐订单与节点开通是两个步骤，请联系管理员开通。": "No node client is linked yet. Placing a plan order and activating a node are separate steps. Contact support to activate it.",
  "节点超过 30 分钟未同步，暂不提供新配置。请稍后刷新或联系管理员。": "The node has not synced for over 30 minutes, so new configuration is unavailable. Refresh later or contact support.",
  "请在浏览器弹窗中允许打开 Clash。导入后选中云流订阅，使用规则模式并开启系统代理；未弹出时可复制链接手动导入。": "Allow your browser to open Clash. Select the Yunliu subscription, use Rule mode, and enable the system proxy. If the app does not open, copy the link and import it manually.",
  "订阅链接已复制，请勿分享。": "Subscription link copied. Do not share it.",
  "浏览器未允许复制，可选中下面的链接手动复制。": "Clipboard access was denied. Select and copy the link below.",
  "旧订阅链接已失效，请重新导入新链接。": "The old subscription link is no longer valid. Import the new link.",
  "先安装兼容客户端": "Install a compatible app first",
  "已验证 Mihomo 1.19.29；1.18.7 需升级。配置已包含 REALITY 兼容参数。": "Mihomo 1.19.29 is verified; upgrade from 1.18.7. The profile includes compatible REALITY parameters.",
  "查看导入步骤": "View import steps",
  "节点实测数据 · ": "Measured node data · ",
  "最近同步 {date}": "Last synced {date}",
  "暂无同步记录": "No sync history",
  "本订阅额度与订单权益独立；Clash 卡片的流量信息在更新订阅时刷新。": "Subscription allowance is separate from order benefits. Clash usage updates when the subscription refreshes.",
  "重置订阅链接": "Reset subscription link",
  "重置后旧订阅链接失效，需要重新导入。已下载的节点凭据不会自动注销；凭据泄露时还需联系管理员更换 UUID。": "The old link will stop working and must be imported again. Downloaded node credentials will not be revoked automatically. Contact support to replace the UUID if credentials were exposed.",
  "重置中…": "Resetting…",
  "确认重置": "Confirm reset",
  "取消": "Cancel",
  "云流体验额度": "Yunliu trial allowance",
  "流量套餐": "Data plan",
  "轻量套餐": "Light plan",
  "适合偶尔追剧、听歌和短期使用": "For occasional streaming, music, or short-term use",
  "标准套餐": "Standard plan",
  "适合日常影音娱乐与稳定使用": "For everyday streaming and reliable access",
  "畅享套餐": "Plus plan",
  "云流广州测试": "Yunliu Guangzhou Test",
  "复制订阅链接": "Copy subscription link",
  "下载配置": "Download configuration",
  "私人订阅链接，勿分享": "Private subscription link. Do not share it.",
  "云流加速订阅": "Yunliu acceleration subscription",
  "云流客户端产品界面预览": "Yunliu app interface preview",
  "智能加速": "Smart acceleration",
  "适合高清影音与多设备重度使用": "For HD streaming and multiple devices",
};

export function translateMessage(locale: Locale, message: string, values?: MessageValues): string {
  const translated = locale === "en-US" ? englishMessages[message] ?? message : message;
  return translated.replace(/\{(\w+)\}/g, (placeholder, key: string) => {
    const value = values?.[key];
    return value === undefined ? placeholder : String(value);
  });
}

const I18nContext = createContext<I18nContextValue | null>(null);

function preferredLocale(): Locale {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === "zh-CN" || saved === "en-US") return saved;
  } catch {
    // Storage can be unavailable in private browsing; fall back to the browser language.
  }
  return typeof navigator !== "undefined" && navigator.language.toLowerCase().startsWith("en") ? "en-US" : "zh-CN";
}

export function I18nProvider({ children }: PropsWithChildren) {
  const [locale, setLocaleState] = useState<Locale>("zh-CN");
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    setLocaleState(preferredLocale());
    setInitialized(true);
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = locale;
    if (initialized) {
      try { window.localStorage.setItem(STORAGE_KEY, locale); } catch { /* Keep the current session usable without storage. */ }
    }
  }, [initialized, locale]);

  const setLocale = useCallback((nextLocale: Locale) => setLocaleState(nextLocale), []);
  const t = useCallback((message: string, values?: MessageValues) => translateMessage(locale, message, values), [locale]);
  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n must be used inside I18nProvider");
  return value;
}
